import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import {
  LayoutDashboard,
  Package,
  Boxes,
  ShoppingCart,
  Users,
  Truck,
  Settings,
  LogOut,
  Menu,
  X,
  PackageOpen,
  Store,
  PackageCheck,
  Wrench,
  UserCog,
  Shield,
  Activity,
  ScanLine,
  BarChart3,
} from "lucide-react";
import { useState } from "react";

export default function Layout() {
  const { user, logout, permissions } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Build navigation based on permissions
  const buildNavigation = () => {
    if (!permissions) return [];
    
    const nav = [];
    
    if (permissions.tableau_de_bord) {
      nav.push({ name: "Tableau de bord", href: "/dashboard", icon: LayoutDashboard });
    }
    if (permissions.stock) {
      nav.push({ name: "Stock", href: "/stock", icon: Package });
      nav.push({ name: "Inventaire", href: "/inventaire", icon: ScanLine });
    }
    if (permissions.backmarket) {
      nav.push({ name: "BackMarket", href: "/backmarket", icon: Store, highlight: true });
    }
    if (permissions.lots_achat) {
      nav.push({ name: "Lots d'achat", href: "/lots-achat", icon: Boxes });
    }
    if (permissions.preparations) {
      nav.push({ name: "Préparations", href: "/preparations", icon: PackageCheck, highlight: true, color: "text-purple-600" });
    }
    if (permissions.atelier) {
      nav.push({ name: "Atelier", href: "/atelier", icon: Wrench, highlight: true, color: "text-amber-600" });
    }
    if (permissions.lots_vente) {
      nav.push({ name: "Lots de vente", href: "/lots-vente", icon: PackageOpen });
    }
    if (permissions.fournisseurs) {
      nav.push({ name: "Fournisseurs", href: "/fournisseurs", icon: Truck });
    }
    if (permissions.clients) {
      nav.push({ name: "Clients", href: "/clients", icon: Users });
    }
    if (permissions.commandes) {
      nav.push({ name: "Commandes", href: "/commandes", icon: ShoppingCart });
    }
    if (permissions.utilisateurs) {
      nav.push({ name: "Utilisateurs", href: "/utilisateurs", icon: UserCog });
    }
    // Add Roles management for admin only
    if (user?.role === "admin") {
      nav.push({ name: "Rôles", href: "/roles", icon: Shield, highlight: true, color: "text-red-600" });
    }
    // Add Activity page for admin and manager
    if (user?.role === "admin" || user?.role === "manager") {
      nav.push({ name: "Activité", href: "/activite", icon: Activity, highlight: true, color: "text-indigo-600" });
      nav.push({ name: "Performance", href: "/performance", icon: BarChart3, highlight: true, color: "text-cyan-600" });
    }
    if (permissions.parametres) {
      nav.push({ name: "Paramètres", href: "/parametres", icon: Settings });
    }
    
    return nav;
  };

  const navigation = buildNavigation();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-border transform transition-transform duration-200 lg:translate-x-0 lg:static lg:z-auto",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-border">
            <h1 className="text-xl font-bold font-heading text-primary">
              EMS Gestion
            </h1>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  cn(
                    "sidebar-item",
                    isActive && "sidebar-item-active bg-blue-50 text-blue-700",
                    item.highlight && !isActive && (item.color || "text-orange-600 hover:bg-orange-50"),
                    item.color && !isActive && "hover:bg-purple-50"
                  )
                }
              >
                <item.icon className={cn("h-5 w-5", item.highlight && (item.color || "text-orange-500"))} />
                {item.name}
              </NavLink>
            ))}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-medium">
                {user?.nom?.charAt(0)?.toUpperCase() || "U"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.nom}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {user?.role === "admin" ? "Administrateur" : 
                   user?.role === "manager" ? "Manager" : 
                   user?.role === "technicien" ? "Technicien" : 
                   user?.role === "staff" ? "Staff" : user?.role || "Utilisateur"}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full justify-start gap-2"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" />
              Déconnexion
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-16 bg-white border-b border-border flex items-center px-4 lg:px-6 sticky top-0 z-30">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden mr-2"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div className="flex-1" />
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
